import type { FC } from '../../lib/teact/teact';
import {
  memo, useEffect, useRef, useState,
} from '../../lib/teact/teact';

import buildClassName from '../../util/buildClassName';
import { CRM_BACKEND_URL as BACKEND_URL } from '../../util/crmBackendUrl';

import useLastCallback from '../../hooks/useLastCallback';

import styles from './ChannelBroadcastView.module.scss';

const OFFICIAL_ADMISSION_URL = import.meta.env.VITE_OFFICIAL_ADMISSION_URL || 'https://t.me/m/zFQChH6MMDI5';

interface ProgramTemplate {
  id: string;
  label: string;
  title: string;
  programName: string;
  startDate: string;
  schedule: string;
  modality: string;
  bodyText: string;
  buttonText: string;
  buttonUrl: string;
  bannerUrl: string;
}

const PROGRAM_TEMPLATES: ProgramTemplate[] = [
  {
    id: 'farmacologia',
    label: 'Diplomado en Farmacología y Terapéutica Médica (1.ª Versión)',
    title: 'DIPLOMADO EN FARMACOLOGÍA Y TERAPÉUTICA MÉDICA',
    programName: '1.ª VERSIÓN',
    startDate: '05 de Octubre de 2026',
    schedule: 'Lunes, Miércoles y Viernes de 19:00 a 22:00',
    modality: 'Sincrónica 100% virtual por Webex',
    bodyText: 'Especialízate en actualización farmacológica, dosificación precisa, interacciones medicamentosas '
      + 'y terapéutica clínica basada en evidencia con docentes de alto nivel de la UMSS.\n\n'
      + '📌 Inversión: 5 cuotas de Bs. 1.200 (10% desc. al contado).\n'
      + '⚠️ Asistencia obligatoria en vivo (las sesiones NO se graban).\n'
      + '📋 Cupos estrictamente limitados.',
    buttonText: '👉 Ver Convocatoria e Inscribirme',
    buttonUrl: OFFICIAL_ADMISSION_URL,
    bannerUrl: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=800&auto=format&fit=crop&q=60',
  },
  {
    id: 'fisioterapia_cardio',
    label: 'Diplomado en Fisioterapia Cardiorrespiratoria y VM (1.ª Versión)',
    title: 'DIPLOMADO EN FISIOTERAPIA CARDIORRESPIRATORIA Y VENTILACIÓN MECÁNICA',
    programName: '1.ª VERSIÓN',
    startDate: '05 de Octubre de 2026',
    schedule: 'Lunes a Jueves de 19:00 a 22:00',
    modality: 'Sincrónica 100% virtual por Webex',
    bodyText: 'Capacitación integral en rehabilitación pulmonar, destete ventilatorio, oxigenoterapia de alto flujo '
      + 'y manejo del paciente crítico en UCI y hospitalización.\n\n'
      + '📌 Inversión: 5 cuotas de Bs. 1.100.\n'
      + '⚠️ Asistencia obligatoria (las clases NO se graban).\n'
      + '📋 Certificación oficial UMSS.',
    buttonText: '👉 Ver Convocatoria e Inscribirme',
    buttonUrl: OFFICIAL_ADMISSION_URL,
    bannerUrl: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800&auto=format&fit=crop&q=60',
  },
  {
    id: 'nutricion_clinica',
    label: 'Diplomado en Nutrición Clínica y Dietoterapia (1.ª Versión)',
    title: 'DIPLOMADO EN NUTRICIÓN CLÍNICA Y DIETOTERAPIA INTEGRAL',
    programName: '1.ª VERSIÓN',
    startDate: '12 de Octubre de 2026',
    schedule: 'Martes y Jueves de 19:00 a 22:00',
    modality: 'Sincrónica 100% virtual por Webex',
    bodyText: 'Diseño de planes nutricionales clínicos, soporte enteral/parenteral y abordaje metabólico '
      + 'de patologías complejas con evidencia científica actualizada.\n\n'
      + '📌 Inversión: 5 cuotas de Bs. 1.000.\n'
      + '⚠️ Clases sincrónicas en vivo no grabadas.\n'
      + '📋 Registro oficial EGEMC - UMSS.',
    buttonText: '👉 Ver Convocatoria e Inscribirme',
    buttonUrl: OFFICIAL_ADMISSION_URL,
    bannerUrl: 'https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=800&auto=format&fit=crop&q=60',
  },
  {
    id: 'fisioterapia_trauma',
    label: 'Diplomado en Fisioterapia en Traumatología (1.ª Versión)',
    title: 'DIPLOMADO EN FISIOTERAPIA EN TRAUMATOLOGÍA Y ORTOPEDIA',
    programName: '1.ª VERSIÓN',
    startDate: '19 de Octubre de 2026',
    schedule: 'Sábados de 08:00 a 14:00',
    modality: 'Sincrónica 100% virtual por Webex',
    bodyText: 'Técnicas avanzadas de rehabilitación osteoarticular, readaptación funcional postquirúrgica '
      + 'y terapia manual basadas en protocolos de vanguardia.\n\n'
      + '📌 Inversión: 5 cuotas de Bs. 1.100.\n'
      + '⚠️ Asistencia obligatoria (sesiones no grabadas).\n'
      + '📋 Cupos limitados por orden de inscripción.',
    buttonText: '👉 Ver Convocatoria e Inscribirme',
    buttonUrl: OFFICIAL_ADMISSION_URL,
    bannerUrl: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&auto=format&fit=crop&q=60',
  },
  {
    id: 'ecografia_general',
    label: 'Diplomado en Ecografía General y Doppler Clínico (1.ª Versión)',
    title: 'DIPLOMADO EN ECOGRAFÍA GENERAL Y DOPPLER CLÍNICO',
    programName: '1.ª VERSIÓN',
    startDate: '26 de Octubre de 2026',
    schedule: 'Lunes a Viernes de 19:00 a 21:00',
    modality: 'Sincrónica 100% virtual por Webex',
    bodyText: 'Diagnóstico ecográfico abdominal, obstétrico, tiroideo y vascular con correlación '
      + 'clínico-quirúrgica y discusión de casos reales de posgrado.\n\n'
      + '📌 Inversión: 5 cuotas de Bs. 1.300.\n'
      + '⚠️ Asistencia 100% obligatoria en vivo (sin grabaciones).\n'
      + '📋 Avalado por la Facultad de Medicina UMSS.',
    buttonText: '👉 Ver Convocatoria e Inscribirme',
    buttonUrl: OFFICIAL_ADMISSION_URL,
    bannerUrl: 'https://images.unsplash.com/photo-1516549655169-df83a0774514?w=800&auto=format&fit=crop&q=60',
  },
  {
    id: 'auditoria_medica',
    label: 'Diplomado en Auditoría Médica y Calidad en Salud (1.ª Versión)',
    title: 'DIPLOMADO EN AUDITORÍA MÉDICA Y GESTIÓN DE LA CALIDAD EN SALUD',
    programName: '1.ª VERSIÓN',
    startDate: '02 de Noviembre de 2026',
    schedule: 'Viernes de 19:00 a 22:00 y Sábados de 08:00 a 13:00',
    modality: 'Sincrónica 100% virtual por Webex',
    bodyText: 'Auditoría en servicio, normatividad PRONACS, peritaje médico-legal y gestión '
      + 'de la seguridad del paciente para profesionales e instituciones de salud.\n\n'
      + '📌 Inversión: 5 cuotas de Bs. 1.200.\n'
      + '⚠️ Asistencia en vivo no grabada.\n'
      + '📋 Certificado con valor curricular de la UMSS.',
    buttonText: '👉 Ver Convocatoria e Inscribirme',
    buttonUrl: OFFICIAL_ADMISSION_URL,
    bannerUrl: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&auto=format&fit=crop&q=60',
  },
];

const ChannelBroadcastView: FC = () => {
  const [selectedTemplate, setSelectedTemplate] = useState('farmacologia');
  const [title, setTitle] = useState(PROGRAM_TEMPLATES[0].title);
  const [programName, setProgramName] = useState(PROGRAM_TEMPLATES[0].programName);
  const [startDate, setStartDate] = useState(PROGRAM_TEMPLATES[0].startDate);
  const [schedule, setSchedule] = useState(PROGRAM_TEMPLATES[0].schedule);
  const [modality, setModality] = useState(PROGRAM_TEMPLATES[0].modality);
  const [bodyText, setBodyText] = useState(PROGRAM_TEMPLATES[0].bodyText);
  const [buttonText, setButtonText] = useState(PROGRAM_TEMPLATES[0].buttonText);
  const [buttonUrl, setButtonUrl] = useState(PROGRAM_TEMPLATES[0].buttonUrl);

  const [bannerMode, setBannerMode] = useState<'url' | 'file'>('url');
  const [bannerUrl, setBannerUrl] = useState(PROGRAM_TEMPLATES[0].bannerUrl);
  const [bannerFile, setBannerFile] = useState<File | undefined>(undefined);
  const [previewImageUrl, setPreviewImageUrl] = useState(PROGRAM_TEMPLATES[0].bannerUrl);

  const [channelTitle, setChannelTitle] = useState('EGEMC - Posgrado Medicina UMSS');
  const [channelMembers, setChannelMembers] = useState(8);

  const [feedback, setFeedback] = useState<string | undefined>(undefined);
  const [publishedPostUrl, setPublishedPostUrl] = useState<string | undefined>(undefined);
  const [isSending, setIsSending] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>();

  // Cargar datos en vivo del canal al montar
  useEffect(() => {
    fetch(`${BACKEND_URL}/channels/telegram`)
      .then((res) => (res.ok ? res.json() : undefined))
      .then((data) => {
        if (!data) return;
        if (data.title) setChannelTitle(data.title);
        if (data.members_count) setChannelMembers(data.members_count);
      })
      .catch(() => {
        // Fallback silencioso
      });
  }, []);

  const previewCaption = `📢 ${title.toUpperCase()}

🎓 ${programName}
Facultad de Medicina «Dr. Aurelio Meleán» — UMSS

🗓️ Inicio: ${startDate}
⏰ Horarios: ${schedule}
🏛️ Modalidad: ${modality} (asistencia obligatoria, clases no se graban)

${bodyText}

👉 Conoce todos los detalles e inscríbete aquí:`;

  const charCount = previewCaption.length;
  const isOverLimit = charCount > 1024;

  const handleSelectTemplate = useLastCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const tplId = e.target.value;
    setSelectedTemplate(tplId);
    const found = PROGRAM_TEMPLATES.find((tpl) => tpl.id === tplId);
    if (!found) return;

    setTitle(found.title);
    setProgramName(found.programName);
    setStartDate(found.startDate);
    setSchedule(found.schedule);
    setModality(found.modality);
    setBodyText(found.bodyText);
    setButtonText(found.buttonText);
    setButtonUrl(found.buttonUrl);
    if (bannerMode === 'url') {
      setBannerUrl(found.bannerUrl);
      setPreviewImageUrl(found.bannerUrl);
    }
  });

  const handleFileChange = useLastCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setBannerFile(file);
    const objectUrl = URL.createObjectURL(file);
    setPreviewImageUrl(objectUrl);
  });

  const handleClearFile = useLastCallback(() => {
    setBannerFile(undefined);
    setPreviewImageUrl(bannerUrl);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  });

  const handleBannerUrlChange = useLastCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setBannerUrl(val);
    if (bannerMode === 'url') {
      setPreviewImageUrl(val);
    }
  });

  const handleBroadcast = useLastCallback(async () => {
    if (isOverLimit) {
      setFeedback('⚠️ El texto supera el límite máximo de 1024 caracteres de pie de foto en Telegram.');
      return;
    }

    setIsSending(true);
    setFeedback('Difundiendo comunicado oficial a @PosgradoMedicinaUMSS vía Telegram API...');
    setPublishedPostUrl(undefined);

    try {
      let res: Response;

      if (bannerMode === 'file' && bannerFile) {
        // Enviar con subida directa de flyer binario (multipart/form-data)
        const formData = new FormData();
        formData.append('title', title);
        formData.append('program_name', programName);
        formData.append('start_date', startDate);
        formData.append('schedule', schedule);
        formData.append('modality', modality);
        formData.append('body_text', bodyText);
        formData.append('button_text', buttonText);
        formData.append('button_url', buttonUrl);
        formData.append('banner_file', bannerFile);

        res = await fetch(`${BACKEND_URL}/channels/telegram/broadcast-file`, {
          method: 'POST',
          body: formData,
        });
      } else {
        // Enviar con URL de imagen remota o texto
        res = await fetch(`${BACKEND_URL}/channels/telegram/broadcast`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title,
            program_name: programName,
            start_date: startDate,
            schedule,
            modality,
            body_text: bodyText,
            button_text: buttonText,
            button_url: buttonUrl,
            banner_url: bannerUrl || undefined,
          }),
        });
      }

      const data = await res.json();

      if (res.ok && data.success) {
        setPublishedPostUrl(data.post_url);
        setFeedback('✅ ¡Comunicado publicado exitosamente en el canal @PosgradoMedicinaUMSS con botón interactivo!');
      } else {
        setFeedback(`Error de Telegram: ${data.detail || 'No se pudo publicar el anuncio en el canal'}`);
      }
    } catch {
      setFeedback('Error de conexión con el backend de difusión.');
    } finally {
      setIsSending(false);
    }
  });

  return (
    <div className={styles.root}>
      <div>
        <h2 className={styles.headerTitle}>
          Diseñador de Comunicados para Canal Oficial
        </h2>
        <p className={styles.headerSubtitle}>
          Generador de posts oficiales para
          {' '}
          <strong>@PosgradoMedicinaUMSS</strong>
          {' '}
          con los 7 bloques canónicos y botón inline interactivo nativo.
        </p>
      </div>

      <div className={styles.layout}>
        {/* Formulario de los 7 Bloques */}
        <div className={styles.formCard}>
          {/* Selector de Plantilla RAG */}
          <div className={styles.templateBox}>
            <span className={styles.templateLabel}>
              📂 Cargar Plantilla desde Programa RAG:
            </span>
            <select
              className={styles.select}
              value={selectedTemplate}
              onChange={handleSelectTemplate}
            >
              {PROGRAM_TEMPLATES.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.label}
                </option>
              ))}
            </select>
          </div>

          <h3 className={styles.cardTitle}>Los 7 Bloques del Comunicado</h3>

          {/* 1. Banner / Imagen */}
          <div className={styles.formGroup}>
            <label className={styles.label}>1. Banner / Imagen del Comunicado:</label>
            <div className={styles.bannerTypeToggle}>
              <button
                type="button"
                className={buildClassName(styles.toggleTab, bannerMode === 'url' && styles.active)}
                onClick={() => {
                  setBannerMode('url');
                  setPreviewImageUrl(bannerUrl);
                }}
              >
                🌐 Enlace URL
              </button>
              <button
                type="button"
                className={buildClassName(styles.toggleTab, bannerMode === 'file' && styles.active)}
                onClick={() => {
                  setBannerMode('file');
                  if (bannerFile) {
                    setPreviewImageUrl(URL.createObjectURL(bannerFile));
                  }
                }}
              >
                📁 Subir Flyer (PC)
              </button>
            </div>

            {bannerMode === 'url' ? (
              <input
                id="b-banner"
                className={styles.input}
                placeholder="https://..."
                value={bannerUrl}
                onChange={handleBannerUrlChange}
              />
            ) : (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className={styles.hiddenFileInput}
                  onChange={handleFileChange}
                />
                {bannerFile ? (
                  <div className={styles.fileBadge}>
                    <span>
                      📷
                      {' '}
                      {bannerFile.name}
                      {' '}
                      (
                      {Math.round(bannerFile.size / 1024)}
                      {' '}
                      KB)
                    </span>
                    <button
                      type="button"
                      className={styles.clearFileBtn}
                      onClick={handleClearFile}
                      title="Quitar imagen"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div
                    className={styles.fileDropZone}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <span className={styles.fileDropText}>
                      📤 Clic aquí para seleccionar el Flyer (JPG/PNG)
                    </span>
                    <span className={styles.fileDropSubtext}>
                      Se enviará directamente a Telegram en alta resolución
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="b-title">2. Encabezado / Programa:</label>
            <input
              id="b-title"
              className={styles.input}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="b-version">Versión del Programa:</label>
            <input
              id="b-version"
              className={styles.input}
              value={programName}
              onChange={(e) => setProgramName(e.target.value)}
            />
          </div>

          <div className={styles.twoColGrid}>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="b-date">3. Fecha de Inicio:</label>
              <input
                id="b-date"
                className={styles.input}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="b-schedule">4. Horarios:</label>
              <input
                id="b-schedule"
                className={styles.input}
                value={schedule}
                onChange={(e) => setSchedule(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="b-modality">5. Modalidad y Aclaraciones:</label>
            <input
              id="b-modality"
              className={styles.input}
              value={modality}
              onChange={(e) => setModality(e.target.value)}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="b-body">6. Cuerpo / Beneficios / Requisitos:</label>
            <textarea
              id="b-body"
              className={styles.textarea}
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
            />
          </div>

          <div className={styles.buttonColGrid}>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="b-btn-text">7. Botón Nativo (Texto):</label>
              <input
                id="b-btn-text"
                className={styles.input}
                value={buttonText}
                onChange={(e) => setButtonText(e.target.value)}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="b-btn-url">Enlace del Botón:</label>
              <input
                id="b-btn-url"
                className={styles.input}
                value={buttonUrl}
                onChange={(e) => setButtonUrl(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Previsualización Nativa del Canal */}
        <div className={styles.previewContainer}>
          <div className={styles.previewCard}>
            <div className={styles.previewHeader}>
              <div className={styles.channelAvatar}>E</div>
              <div className={styles.channelInfo}>
                <span className={styles.channelName}>{channelTitle}</span>
                <span className={styles.channelSub}>
                  Canal Oficial •
                  {' '}
                  {channelMembers}
                  {' '}
                  suscriptores
                </span>
              </div>
            </div>

            {previewImageUrl && (
              <img src={previewImageUrl} alt="Banner Comunicado" className={styles.previewImage} />
            )}

            <div className={styles.previewText}>
              {previewCaption}
            </div>

            <a
              href={buttonUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.inlineButton}
            >
              {buttonText}
              {' '}
              ↗
            </a>
          </div>

          <div className={buildClassName(styles.counterBadge, isOverLimit && styles.warning)}>
            Caracteres:
            {' '}
            {charCount}
            {' '}
            / 1024
            {' '}
            {isOverLimit ? '(¡Excedido!)' : '(Válido)'}
          </div>

          {feedback && (
            <div className={feedback.includes('✅') ? styles.feedbackSuccess : styles.feedbackError}>
              {feedback}
            </div>
          )}

          {publishedPostUrl && (
            <div className={styles.postSuccessCard}>
              <span className={styles.postSuccessText}>
                🚀 ¡Comunicado transmitido en vivo al canal!
              </span>
              <a
                href={publishedPostUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.postSuccessLink}
              >
                Abrir publicación en Telegram ↗
              </a>
            </div>
          )}

          <button
            type="button"
            className={styles.broadcastButton}
            onClick={handleBroadcast}
            disabled={isSending || isOverLimit}
          >
            {isSending ? 'Difundiendo al Canal...' : '📢 Difundir al Canal Oficial de Telegram'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default memo(ChannelBroadcastView);
